import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Chatbot } from "@/components/Chatbot";
import { AssistantProvider } from "@/contexts/AssistantContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { TutorialProvider } from "@/contexts/TutorialContext";
import { TutorialModal, TutorialDrawer } from "@/components/tutorials";
import { AppRoutes } from "@/routes";
import { AppUpdateProvider } from "@/contexts/AppUpdateContext";
import { AppUpdateBanner } from "@/components/AppUpdateBanner";
import { SentryRouteContext } from "@/components/SentryRouteContext";
import { onMutationError, onQueryError } from "@/lib/sentry";

const isDemo = import.meta.env.VITE_IS_DEMO === "true";

// Create QueryClient instance
const queryClient = new QueryClient({
  // Relais vers Sentry des échecs rattrapés par les hooks (voir src/lib/sentry.ts)
  queryCache: new QueryCache({ onError: onQueryError }),
  mutationCache: new MutationCache({ onError: onMutationError }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Main App component
 *
 * Architecture:
 * - BrowserRouter: Client-side routing
 * - QueryClientProvider: React Query for server state
 * - ThemeProvider: Dark/light mode via next-themes
 * - AuthProvider: Authentication context
 * - TooltipProvider: Radix UI tooltips
 * - AppRoutes: Modular route definitions
 * - Chatbot: AI assistant widget
 * - Toaster: Toast notifications (shadcn + sonner)
 */
function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={<p className="p-8 text-center text-destructive">Une erreur est survenue. L'équipe a été notifiée.</p>}
      showDialog
    >
    <HelmetProvider>
    <BrowserRouter>
      <SentryRouteContext />
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="light" storageKey="pala-theme">
        {/* Detecte la version reellement deployee (sans jamais recharger tout seul) */}
        <AppUpdateProvider>
        <AuthProvider>
          <TutorialProvider>
            <AssistantProvider>
            <TooltipProvider>
              {/* Main application routes */}
              <AppRoutes />

              {/* Tutorial Modal & Drawer (global) */}
              <TutorialModal />
              <TutorialDrawer />

              {/* Global UI components — demo only */}
              {isDemo && <Chatbot />}
              {/* Default shadcn toaster */}
              <Toaster />
              {/* Sonner toaster for more complex notifications */}
              <Sonner />

              {/* Propose la mise a jour ; rien n'est recharge sans action de l'utilisateur */}
              <AppUpdateBanner />
            </TooltipProvider>
            </AssistantProvider>
          </TutorialProvider>
        </AuthProvider>
        </AppUpdateProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
    </HelmetProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
